import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_V1, getImageUrl, fetchApi, fetchApiWithoutBranch } from '../config/api';
import { getStoredBranch, SELECTED_BRANCH_STORAGE_KEY } from './BranchSelectModal';
import { refAmount } from '../utils/money';
import './Header.css';
import logo from '../assets/Isologo/Isologo_ChefMargaro_2022.png';
import cartIcon from '../assets/icons/cart.png';
import storeIcon from '../assets/icons/store.png';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PER_PAGE = 15;

function normalizeSearchCombo(c) {
  return {
    id: c.id ?? c.combo_id,
    name: c.name ?? 'Combo',
    price: refAmount(c.price_eur),
    image: getImageUrl(c.image_url ?? c.thumbnail ?? c.thumbnail_url ?? c.image),
  };
}

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(() => getStoredBranch());
  const [branchList, setBranchList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const branchDropdownRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const { toggleCart, getTotalPrice, getTotalItems } = useCart();
  const cartHasItems = getTotalItems() > 0;
  const cartButtonClassName = `cart-button${cartHasItems ? ' cart-button--pulse cart-button--highlight' : ''}`;

  const fetchSearch = useCallback((query) => {
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    searchAbortRef.current = new AbortController();
    setSearchLoading(true);
    const params = new URLSearchParams({
      search: query.trim(),
      per_page: String(SEARCH_PER_PAGE),
      is_active: '1',
    });
    fetchApi(`${API_V1}/combos?${params.toString()}`, {
      signal: searchAbortRef.current.signal,
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Search failed'))))
      .then((data) => {
        const list = data.data ?? data;
        const raw = Array.isArray(list) ? list : (list?.data ?? []);
        setSearchResults(raw.map(normalizeSearchCombo).filter((c) => c.id != null));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setSearchResults([]);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchDropdownOpen(false);
      return;
    }
    setSearchDropdownOpen(true);
    searchDebounceRef.current = setTimeout(() => {
      fetchSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, fetchSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchProductClick = useCallback(
    (comboId) => {
      setSearchQuery('');
      setSearchDropdownOpen(false);
      setSearchResults([]);
      navigate(`/combo/${comboId}`);
    },
    [navigate]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setIsBranchDropdownOpen(false);
      }
    };

    if (isBranchDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBranchDropdownOpen]);

  useEffect(() => {
    const handleBranchUpdated = () => setSelectedBranch(getStoredBranch());
    window.addEventListener('branchUpdated', handleBranchUpdated);
    return () => window.removeEventListener('branchUpdated', handleBranchUpdated);
  }, []);

  useEffect(() => {
    // Usar fetchApiWithoutBranch para obtener TODAS las sucursales sin filtrar por X-Branch-Id
    fetchApiWithoutBranch(`${API_V1}/branches?search=&per_page=20`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        const list = data.data ?? data;
        setBranchList(Array.isArray(list) ? list : []);
      })
      .catch(() => setBranchList([]));
  }, []);

  const handleSelectBranch = (branch) => {
    const toStore = {
      id: branch.id ?? branch.branch_id,
      name: branch.name ?? branch.branch_name ?? 'Sucursal',
      address: branch.address ?? branch.address_detail ?? '',
      ...branch,
    };
    try {
      localStorage.setItem(SELECTED_BRANCH_STORAGE_KEY, JSON.stringify(toStore));
      setSelectedBranch(toStore);
      window.dispatchEvent(new CustomEvent('branchUpdated'));
      setIsBranchDropdownOpen(false);
      // Recargar la página para aplicar el filtro de sucursal
      window.location.reload();
    } catch (e) {
      // ignore
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-content">

          <div className="top-bar-right">
            <div className="social-icons">
              <a href="mailto:contacto@chefmargaro.com" aria-label="Email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/chefmargaro/?hl=en" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a href="http://wa.me/584126274276" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
              <a href="https://www.tiktok.com/@chefmargaro?lang=en" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                </svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=100092081379704" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>
          <a className="top-bar-text" href="#stores">
            ABRIMOS TODOS LOS DÍAS | Click para ver las sucursales
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="main-nav">
        <div className="nav-content">
          <div className="nav-top">
            <div className="nav-left">
              <div className="logo">
                <button
                  className={`mobile-menu-toggle ${isMenuOpen ? 'open' : ''}`}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
                <div className="logo-circle">
                  <img src={logo} alt="Logo" className="logo-image" />
                </div>
                <span className="logo-text">MARGARO</span>
                <button className={`${cartButtonClassName} cart-button-mobile`} onClick={toggleCart}>
                  <img src={cartIcon} alt="Cart" className="cart-icon" />
                  <span className="cart-ref">Ref</span>
                  <span className="cart-price">{getTotalPrice()}</span>
                  <span className="cart-count">({getTotalItems()})</span>
                </button>
              </div>
              <div className="search-container" ref={searchContainerRef}>
                <div className="search-bar">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="search"
                    placeholder="Buscar..."
                    className="search-bar-text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim() && setSearchDropdownOpen(true)}
                    aria-label="Buscar productos"
                    aria-expanded={searchDropdownOpen}
                    aria-controls="search-results-dropdown"
                    id="header-search-input"
                  />
                </div>
                {searchDropdownOpen && searchQuery.trim() && (
                  <div
                    id="search-results-dropdown"
                    className="search-results-dropdown"
                    role="listbox"
                    aria-label="Resultados de búsqueda"
                  >
                    {searchLoading ? (
                      <div className="search-results-loading">Buscando…</div>
                    ) : searchResults.length === 0 ? (
                      <div className="search-results-empty">No hay resultados.</div>
                    ) : (
                      <ul className="search-results-list">
                        {searchResults.map((product) => (
                          <li
                            key={product.id}
                            role="option"
                            className="search-result-item"
                            onClick={() => handleSearchProductClick(product.id)}
                          >
                            <div className="search-result-image-wrap">
                              <img
                                src={product.image || ''}
                                alt=""
                                className="search-result-image"
                              />
                            </div>
                            <div className="search-result-info">
                              <span className="search-result-name">{product.name}</span>
                              <span className="search-result-price">Ref {product.price}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <button className={`${cartButtonClassName} cart-button-desktop`} onClick={toggleCart}>
                  <img src={cartIcon} alt="Cart" className="cart-icon" />
                  <span className="cart-ref">Ref</span>
                  <span className="cart-price">{getTotalPrice()}</span>
                  <span className="cart-count">({getTotalItems()})</span>
                </button>
              </div>
            </div>
          </div>

          <div className="nav-bottom">
            <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
              <Link to="/">INICIO</Link>
              <Link to="/categorias">CATEGORÍAS</Link>
              <Link to="/combos">COMBOS</Link>
              <Link to="/nosotros">NOSOTROS</Link>
              <Link to="/contactanos">CONTÁCTANOS</Link>
            </div>

            <div className="nav-right">
              <div className="branch-selector-container" ref={branchDropdownRef}>
                <img src={storeIcon} alt="Store" className="store-icon-image" />
                <div className="branch-selector-content" onClick={() => setIsBranchDropdownOpen(!isBranchDropdownOpen)}>
                  <div className="branch-selector-label">ELIGE TU SUCURSAL MÁS CERCANA</div>
                  <div className="branch-selector-text">
                    {selectedBranch ? (selectedBranch.name || selectedBranch.branch_name || 'Sucursal') : 'SELECCIONAR SUCURSAL'}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="branch-chevron">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
                {isBranchDropdownOpen && (
                  <div className="branch-dropdown">
                    {branchList.length === 0 ? (
                      <div className="branch-option branch-option--muted">Cargando sucursales...</div>
                    ) : (
                      branchList.map((b) => {
                        const id = b.id ?? b.branch_id;
                        const name = b.name ?? b.branch_name ?? `Sucursal ${id}`;
                        return (
                          <div key={id} className="branch-option" onClick={() => handleSelectBranch(b)}>
                            {name}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;

